import { ReactNode } from "react"
import { Field } from "formik"
import { useTranslation } from "react-i18next"

import { supportedCurrencies } from "../data"

function add(a: string | number, b: string | number) {
    const an = parseFloat(String(a))
    const bn = parseFloat(String(b))
    return ((isNaN(an) ? 0.0 : an) + (isNaN(bn) ? 0.0 : bn)).toFixed(2)
}

/** Shared body of the cost entry form: title + amount / currency / VAT / total + comment.
 *  Callers render their own top fields (entity link, date, invoice #, cost center) via
 *  `topFields`, since those differ between the per-entity cost form (which has a
 *  ReferenceLinker) and the fixed IP-management form. */
export function CostFormFields({ values, isEdit, topFields }: {
    values: { amount?: string | number, vat?: string | number, currency?: string },
    isEdit: boolean,
    topFields: ReactNode,
}) {
    const { t } = useTranslation()
    return (
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <h2 className="sm:col-span-2">{t(isEdit ? 'edit-cost' : 'add-cost')}</h2>

            {topFields}

            <div className="flex flex-row gap-4 max-sm:flex-wrap sm:col-span-2">
                <div>
                    <label htmlFor="amount" className="label mb-1">{t('amount-excl-vat')}</label>
                    <Field id="amount" name="amount" type="number" required className="form-input w-36" />
                </div>
                <div>
                    <label htmlFor="currency" className="label mb-1">{t('currency')}</label>
                    <Field id="currency" name="currency" className="form-select" as="select">
                        {supportedCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
                    </Field>
                </div>
                <div>
                    <label htmlFor="vat" className="label mb-1">{t('vat')}</label>
                    <Field id="vat" name="vat" type="number" className="form-input w-24" />
                </div>
                <div>
                    <div className="label mb-1">{t('total')}</div>
                    <div className="py-2 px-1 label">{add(values.amount ?? 0, values.vat ?? 0)} {values.currency}</div>
                </div>
            </div>

            <div className="sm:col-span-2">
                <label htmlFor="comment" className="label mb-1">{t('comment')}</label>
                <Field id="comment" name="comment" as="textarea" className="w-full form-textarea h-36" />
            </div>
        </div>
    )
}

/** Shared Save/Cancel footer for the cost form. The cancel control differs per caller
 *  (a route `<Link>` for the per-entity form, an `onCancel` button for the modal form). */
export function CostFormFooter({ cancel }: { cancel: ReactNode }) {
    const { t } = useTranslation()
    return (
        <div className="p-4 bg-pcx-200 flex flex-row-reverse gap-4">
            <input type="submit" className="btn-primary" value={t('save')} />
            {cancel}
        </div>
    )
}
